import { currentPartner } from '@/config/current-partner';
import { getDashboardCometToken, isTagadaAdsHost, TOKEN_PARAM } from '@/lib/analytics/cometly';

const currentHostname = (): string => (typeof window !== 'undefined' ? window.location.hostname : '');

/**
 * `url` with the Cometly journey (`comet_token_override`) when it leaves
 * Tagada's own CRM for Tagada's dashboard: the dashboard pixel adopts it, so
 * the onboarding → live events land on the ad that brought the merchant
 * instead of a "referral from app.tagada.io". Never on a partner CRM or a
 * partner dashboard, never without a known journey.
 */
export function withDashboardCometToken(url: string, toPartnerDashboard = false): string {
  if (toPartnerDashboard) return url;
  if (!isTagadaAdsHost(currentHostname(), import.meta.env.VITE_BRAND as string | undefined)) return url;
  const token = getDashboardCometToken();
  if (!token) return url;
  const target = new URL(url);
  target.searchParams.set(TOKEN_PARAM, token);
  return target.toString();
}

/**
 * TagadaPay merchant dashboard origin for the current CRM host.
 * Dev hosts (app.tagada.dev, localhost) target the dev dashboard. A partner CRM
 * whose partner has its own dashboard targets that dashboard.
 */
export function getTagadaDashboardUrl(
  path = '/merchant/requests/new',
  params?: Record<string, string | null | undefined>,
): string {
  const hostname = currentHostname();
  const isDev = hostname.endsWith('.dev') || hostname === 'localhost' || hostname.endsWith('.local');
  const partnerDashboardHost = currentPartner()?.dashboardHost;
  const base = isDev
    ? 'https://dashboard.tagadapay.dev'
    : partnerDashboardHost
      ? `https://${partnerDashboardHost}`
      : 'https://dashboard.tagadapay.io';

  // `from=crm` tags the handoff: without it a request opened from the CRM is
  // indistinguishable from a direct landing signup, and we cannot tell
  // whether this path converts at all.
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value) query.set(key, value);
  }
  const qs = query.toString();
  return withDashboardCometToken(
    `${base}${path}${qs ? `?${qs}` : ''}`,
    !isDev && Boolean(partnerDashboardHost),
  );
}

export function getTagadaTpaDashboardUrl(tpaId: string): string {
  return getTagadaDashboardUrl(`/merchant/tpa/${encodeURIComponent(tpaId)}`, { from: 'crm' });
}
